/**
 * Shared shell constants for the floating chat surfaces — the canvas panel
 * (`CanvasFloatingChatPanel`) and the home/workspace panel
 * (`WorkspaceFloatingChatPanel`) render the same chrome from these so the two
 * surfaces can't drift. Surface-specific numbers (canvas toolbar clearances,
 * workspace `<main>` reservation caps, sizeClass defaults/keys) stay with their
 * panels.
 */

/** Matches shadcn `PopoverContent` (tailwind `animate-in` / `zoom-in-95` / `slide-in-from-*-2`). */
export const FLOATING_CHAT_MOTION_TRANSITION = {
  duration: 0.2,
  ease: [0.16, 1, 0.3, 1] as const,
};

export const FLOATING_CHAT_MOTION_INITIAL = {
  opacity: 0,
  scale: 0.95,
  x: 8,
  y: 8,
};

export const FLOATING_CHAT_MIN_HEIGHT_PX = 400;
export const FLOATING_CHAT_MIN_WIDTH_PX = 400;
export const FLOATING_CHAT_INITIAL_WIDTH_PX = 510;

/**
 * First-open width of the chat when it docks to the LEFT beside a full-width
 * detail pane (two-pane layout). Matches the staging dashboard-comp
 * `CHAT_EXPANDED_W` (= `--bin-w` * 2 - `--space-12`). The dock is resizable;
 * a dragged width persists under the surface's storage key.
 */
export const FLOATING_CHAT_DOCK_INITIAL_WIDTH_PX = 512;

/** Total vertical inset so a floating panel stays inside its content region (not under chrome). */
export const FLOATING_CHAT_REGION_VERTICAL_INSET_PX = 16;

/**
 * Width left for the expanded right pane when the chat docks beside it. Matches
 * the pane's own `minWidth`, so the dock can't grow past the point where the
 * pane would clamp.
 */
export const MIN_EXPANDED_PANE_WIDTH_PX = 400;

/** Windowed chat floating in the region's bottom-right corner. */
export const CORNER_SHELL_BASE_CLASS =
  "absolute right-2 bottom-2 z-30 origin-bottom-right";

/**
 * Full-height dock: flush to the right edge and to the shell's top + bottom
 * (`inset-y-0`, no vertical inset) so it spans the full region height and lines
 * up with the detail/right pane. A definite-height `flex` wrapper is what makes
 * the inner panel fill vertically — the same trick the RightPane dock relies on
 * (ResizablePanel only manages height when a vertical handle exists, so here the
 * height comes from the flex parent + `h-full` on the panel, not the panel).
 */
export const DOCKED_SHELL_BASE_CLASS =
  "absolute right-0 inset-y-0 z-30 flex origin-right";

/**
 * Below-400px container escape: pin to the viewport instead of the (too narrow)
 * region. Only meaningful under a CSS `@container` ancestor — canvas composes it
 * (the React Flow wrapper is `@container`); the workspace region isn't one, so
 * its shells use the bases alone.
 */
export const SHELL_CONTAINER_FIXED_ESCAPE_CLASS =
  "@max-[399px]:fixed @max-[399px]:z-50";

/**
 * Floating window chrome: rounded-xl + hairline edge + elev-3.
 * `overflow-hidden` clips the rounded corners; the resize grab zones are
 * invisible (handles hidden) so nothing visible is lost — the 2px that
 * overhangs the edge is just unused.
 */
export const FLOATING_WINDOW_PANEL_CLASS =
  "relative overflow-hidden rounded-xl border border-n-10 shadow-elev-3";

/**
 * Docked panel chrome: fill the flex shell (`h-full`), square the edges, drop
 * the border, lift with an edge-only shadow. No `overflow-hidden` here — the
 * edge drag-handle pill sits just outside the panel edge and would be clipped
 * (the inner content already clips itself).
 */
export const DOCKED_PANEL_CLASS =
  "relative h-full rounded-none border-0 shadow-[var(--overlay-edge-shadow)]";
